import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { CocoCelebrationService } from '@coin/chat-bot-util-types';
import { SnowflakeComponent } from '../snowflake/snowflake.component';
import { StarComponent } from '../star/star.component';
import { LanternComponent } from '../lantern/lantern.component';

interface ParticleData {
  id: number;
  x: number;
  y: number;
  size: number;
  duration: number;
  delay: number;
  variant: number;
}

@Component({
  selector: 'coin-celebration-animation',
  templateUrl: './celebration-animation.component.html',
  styleUrl: './celebration-animation.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SnowflakeComponent, StarComponent, LanternComponent],
  host: { style: 'display: contents' }
})
export class CelebrationAnimationComponent {
  private readonly celebrationManager = inject(CocoCelebrationService);

  hasMessages = input<boolean>(false);
  isFaqScreenActive = input<boolean>(false);

  protected readonly animationType = signal<string | null>(this.celebrationManager.getAnimation());
  protected readonly showBatAnimation = computed(() => this.animationType() === 'bat');
  protected readonly particles = signal<ParticleData[]>([]);

  constructor() {
    const animation = this.animationType();
    if (animation === 'snow' || animation === 'star' || animation === 'lantern') {
      this.generateParticles();
    }
  }

  private generateParticles(): void {
    const animation = this.animationType();
    const isLantern = animation === 'lantern';
    const isStar = animation === 'star';
    const countMap = { lantern: 8, star: 30 };
    const count = countMap[animation as keyof typeof countMap] ?? 50;
    const particleSize = this.getParticleSize(isLantern, isStar);
    const particles: ParticleData[] = Array.from({ length: count }, (_, i) => ({
      id: i,
      x: isLantern ? Math.random() * 80 + 10 : Math.random() * 90 + 5,
      y: isLantern ? Math.random() * 60 + 10 : 0,
      size: particleSize(),
      duration: isLantern ? Math.random() * 10 + 20 : Math.random() * 5 + 10,
      delay: isLantern ? Math.random() * 15 : Math.random() * 10,
      variant: Math.floor(Math.random() * 5) + 1
    }));
    this.particles.set(particles);
  }

  private getParticleSize(isLantern: boolean, isStar: boolean): () => number {
    if (isLantern) return () => Math.random() * 10 + 16;
    if (isStar) return () => Math.random() * 10 + 10;
    return () => Math.random() * 20 + 15;
  }
}
